/*
 * Copyright 2017-present Evernote Corporation. All rights reserved.
 */

import queryString from 'query-string';

import BaseEmbedLoader from 'ion-config/loaders/BaseEmbedLoader';

import ENConfig from '../../../config';

const originMatcher = new RegExp(
  '^(https?:\\/\\/)?' + // protocol
  '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.?)+[a-z]{2,}|' + // domain name
  '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
    '(\\:\\d+)?$', //port
  'i'
);
const httpsProtocolMatcher = /^(https?:\/\/)?/gi;

const unsubstitutedValueMatcher = /^{%.*%}$/;

class BaseUrlUtil {
  baseUrl?: string;
  userSlotUrlPrefix: string | null | undefined;

  constructor() {
    /*
     * Grab service config values from the DOM directly rather than calling ion-config,
     * since ion-config may depend on this module to construct a URL to hit
     * /IonOpts.action.
     */
    BaseEmbedLoader.init();
  }

  getBaseUrlFromBaseEmbed(): string | null | undefined {
    const baseUrl = BaseEmbedLoader.getValue('SERVICE_BASE_URL');
    return originMatcher.test(baseUrl) ? baseUrl : null;
  }

  getUserSlotUrlPrefixFromBaseEmbed(): string | null | undefined {
    const userSlotUrlPrefix = BaseEmbedLoader.getValue('userSlotUrlPrefix');
    return unsubstitutedValueMatcher.test(userSlotUrlPrefix) ? null : userSlotUrlPrefix;
  }

  setBaseUrl(url: string) {
    this.baseUrl = url;
    if (!this.baseUrl.endsWith('/')) {
      this.baseUrl += '/';
    }
  }

  getBaseUrl(): string {
    if (this.baseUrl == null) {
      const url = ENConfig.baseUrl || 'https://app.yinxiang.com';
      this.setBaseUrl(url);
    }
    return this.baseUrl as string;
  }

  getBaseUrlHost(): string {
    return new URL(this.getBaseUrl()).host;
  }

  /**
   * Gets a URL prefix to use for all service URLs to support the multiauth feature. May
   * be a blank string if the user is only authenticated to a single account. If this
   * string is not blank, then it must have a leading slash, and must not have a trailing
   * slash.
   *
   * @return a URL prefix that should be added to all service URLs
   */
  getUserSlotUrlPrefix(): string {
    if (this.userSlotUrlPrefix == null) {
      /*
       * The query string fallback is intended for dev mode, where we cannot pass a slot
       * prefix in the URL path if we're using the dev server.
       */
      this.userSlotUrlPrefix =
        this.getUserSlotUrlPrefixFromBaseEmbed() ||
        String(
          (typeof window === 'object' &&
            window.location &&
            queryString.parse(window.location.search).USER_SLOT_URL_PREFIX) ||
            ''
        );
    }
    return this.userSlotUrlPrefix;
  }

  /**
   * Concatenates the provided path prefix to the end of getBaseUrl, removing any
   * duplicate leading slashes on the path and ensuring that a trailing comma still exists
   * in the returned URL. Examples of a path prefixes include a user slot prefix (e.g.
   * "u/1/") and a shard prefix (e.g. "shard/s2/"). This method is useful for
   * concatenating path prefixes that may have inconsistent leading or trailing slashes.
   *
   * @return a combined base URL and path prefix with a trailing slash
   */
  getBaseUrlWithPathPrefix(): string {
    const pathPrefixNoLeadingSlash = () => {
      const match = /^\/*(.*)$/.exec(this.getUserSlotUrlPrefix());
      return match ? match[1] : this.getUserSlotUrlPrefix();
    };

    let url = `${this.getBaseUrl()}${pathPrefixNoLeadingSlash()}`;
    if (!url.endsWith('/')) {
      url += '/';
    }
    return url;
  }

  getCoreServiceHostname(): string {
    const httpslessHostname = this.getBaseUrl().replace(httpsProtocolMatcher, '');
    return httpslessHostname.endsWith('/')
      ? httpslessHostname.slice(0, -1)
      : httpslessHostname;
  }
}

export default new BaseUrlUtil();
